import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import useFlash from "@hooks/use-flash";
import { useSubscriptionStatus, useCurrentUser } from "@hooks/use-current-user";
import { useReloadUser } from "@hooks/use-modify-user";
import { useChangeSubscriptionPlan } from "@hooks/use-subscription";
import { usePrice } from "@hooks/use-prices";
import Button from "@components/core/Button";
import { subscriptionStatuses } from "@utils/constants/subscriptionStatuses";
import { getFeaturedPlan } from "@utils/call-api/plan-call";

function ChangeSubscription() {
  const t = useTranslations("accounts");

  const addFlash = useFlash();
  const user = useCurrentUser();
  const reloadUser = useReloadUser();
  const changeSubscriptionPlan = useChangeSubscriptionPlan();
  const subscriptionStatus = useSubscriptionStatus();
  const price = usePrice();

  const [featuredPlan, setFeaturedPlan] = useState({});
  const [loading, setLoading] = useState(false);

  const userCanChangePlan =
    user?.subscription?.plan_price?.plan?.can_change &&
    subscriptionStatus === subscriptionStatuses.subscribed;

  async function changeToNewPlan() {
    if (loading) return;

    try {
      setLoading(true);
      await changeSubscriptionPlan();
      reloadUser();
      addFlash(t("manage_subscription.change_plan.success"), { variant: "success" }, true);
    } catch (err) {
      addFlash(t("manage_subscription.change_plan.error"), { variant: "danger" }, true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function initFeaturedPlan() {
      const resFeaturedPlan = await getFeaturedPlan();
      setFeaturedPlan(resFeaturedPlan);
    }

    initFeaturedPlan();
  }, []);

  if (!userCanChangePlan) return null;

  return (
    <div
      className="bg-white p-3 d-flex flex-column align-items-center rounded-lg"
      data-testid="manage-subscription-change-section"
    >
      <h5 className="text-center mb-2">{t("manage_subscription.change_plan.title")}</h5>
      <p className="text-center">
        {t(
          `manage_subscription.change_plan.description${featuredPlan.unlimited ? "" : "_limited"}`,
          {
            itemsCount: featuredPlan.clothes + featuredPlan.accessories,
            price: price(featuredPlan.amount_inc_tax, { withoutSpace: true }),
          },
        )}
        {!featuredPlan.unlimited && (
          <span>{t("manage_subscription.change_plan.first_month_free")}</span>
        )}
      </p>

      <Button
        onClick={changeToNewPlan}
        loading={loading}
      >
        {t("manage_subscription.change_plan.button")}
      </Button>
    </div>
  );
}

export default ChangeSubscription;
